import { appConfigs } from 'shared/configs/appConfigs'
import { logger } from 'shared/services/logger'
import { isProd } from 'shared/utils/isProd'

const UPDATE_URL_PROD =
  'https://microfrontend-update-note.innoscripta.com/production/update.json'
const UPDATE_URL_TEST =
  'https://microfrontend-update-note.innoscripta.com/testing/update.json'

/**
 * Total number of cached entries across every Cache Storage bucket the
 * origin owns (container assets, federation modules, API responses, etc).
 * Returns 0 when the Cache API is unavailable.
 */
export async function getCachedItemCount() {
  if (typeof caches === 'undefined') return 0

  const cacheNames = await caches.keys()
  const counts = await Promise.all(
    cacheNames.map(async name => {
      const cache = await caches.open(name)
      const keys = await cache.keys()
      return keys.length
    })
  )
  return counts.reduce((total, count) => total + count, 0)
}

/**
 * Clear every Cache Storage bucket without touching service worker
 * registrations. Use this when the user wants to drop cached responses
 * while leaving caching (and its associated SWs) turned on.
 */
export async function deleteAllCaches() {
  if (typeof caches === 'undefined') return

  const cacheNames = await caches.keys()
  await Promise.all(
    cacheNames.map(name => {
      logger.debug('service_worker.cache_delete_requested', { cacheName: name })
      return caches.delete(name)
    })
  )
}

/**
 * Kill switch: unregister all service workers and clear all caches.
 * Used when the user disables caching via the Preferences toggle.
 */
export async function nukeServiceWorkersAndCaches() {
  if (!('serviceWorker' in navigator)) return

  const registrations = await navigator.serviceWorker.getRegistrations()
  const unregisterPromises = registrations.map(reg => {
    logger.debug('service_worker.unregister_requested', { scope: reg.scope })
    return reg.unregister()
  })

  const cacheNames = await caches.keys()
  const cacheDeletePromises = cacheNames.map(name => {
    logger.debug('service_worker.cache_delete_requested', { cacheName: name })
    return caches.delete(name)
  })

  await Promise.all([...unregisterPromises, ...cacheDeletePromises])
  logger.track('service_worker.reset_completed', {
    registrationCount: registrations.length,
    cacheCount: cacheNames.length
  })
}

export function registerRootServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    logger.warn('service_worker.unsupported', {})
    return
  }

  navigator.serviceWorker.register('/root-sw.js').catch(error => {
    logger.exception(
      'service_worker.registration_failed',
      error,
      { scope: '/root-sw.js' },
      { category: 'service-worker' }
    )
  })
}

export function registerFederationAppPaths(paths: string[]) {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.ready.then(registration => {
    registration.active?.postMessage({
      type: 'REGISTER_APP_PATHS',
      paths
    })
  })
}

export function registerFederationAppConfigs(
  configs: Array<{
    id: string
    path: string
    federationUrl?: string
    cdnUrl?: string
  }>,
  updateUrl?: string
) {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.ready.then(registration => {
    registration.active?.postMessage({
      type: 'REGISTER_APP_CONFIGS',
      configs,
      updateUrl
    })
  })
}

/**
 * Update the federation SW's app-limit filter at runtime.
 * - `appIds = null` → no limit (cache all apps)
 * - `appIds = []`   → limit to no apps (federation caching effectively off)
 * - `appIds = [...]` → limit to these apps only
 */
export function setServiceWorkerAppLimit(appIds: string[] | null) {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.ready.then(registration => {
    registration.active?.postMessage({
      type: 'SET_APP_LIMIT',
      appIds
    })
  })
}

export function registerNotificationServiceWorker() {
  registerRootServiceWorker()
}

export function registerLiveChatServiceWorker() {
  registerRootServiceWorker()
}

/**
 * Start up all container-level service workers and push the federation app
 * configs to the active SW. Used both at app boot and when the user
 * re-enables caching from the Preferences dialog.
 *
 * If `limitedAppIds` is provided, also pushes an app-limit override so the
 * SW caches only those apps. Pass `null` to explicitly clear the limit.
 * Omit the arg to leave the SW's current limit untouched.
 */
export function startServiceWorkers(limitedAppIds?: string[] | null) {
  registerRootServiceWorker()

  const configs = Object.values(appConfigs).map(config => ({
    id: config.id,
    path: config.path,
    federationUrl: config.federationUrl,
    cdnUrl: config.cdnUrl
  }))
  const updateUrl = isProd() ? UPDATE_URL_PROD : UPDATE_URL_TEST
  registerFederationAppConfigs(configs, updateUrl)

  if (limitedAppIds !== undefined) {
    setServiceWorkerAppLimit(limitedAppIds)
  }
}
